import { wrapCreateBrowserRouterV6 } from '@sentry/react'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { createBrowserRouter, Navigate, RouterProvider, Outlet, ScrollRestoration } from 'react-router-dom'

import {
  AuthGuard,
  ConsumerAuthGuard,
  EnterpriseAuthGuard,
  EnterpriseSignInFlowGuard,
} from 'src/app/router/guards/auth'
import {
  EnterpriseOnboardingPageGuard,
  EnterpriseOnboardingGuard,
  OnboardingGuard,
} from 'src/app/router/guards/onboarding'
import AuthLayout from 'src/app/router/layouts/auth-layout'
import AuthenticatedLayout from 'src/app/router/layouts/authenticated-layout'
import PublicLayout from 'src/app/router/layouts/public-layout'
import ReceivedReferralSharePage from 'src/app/router/routes/business/referrals/detail/received-referral-share'
import SentReferralPage from 'src/app/router/routes/business/referrals/detail/sent-referral'
import ErrorPage from 'src/app/router/routes/error'
import UserProfile from 'src/app/router/routes/user/profile'
import { paths } from 'src/config/paths'
import { AppErrorBoundary } from 'src/shared/components/errors/app-error-boundary'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { GenericSuspense } from 'src/shared/components/ui/miscellaneous/generic-suspense'
import EnterpriseAuthLayout from 'src/shared/features/enterprise/auth/layout'
import { usePageView } from 'src/shared/hooks/use-page-view'
import { useTrackingParams } from 'src/shared/hooks/use-tracking-params'

import BlockWebGuard from './guards/block-web-guard'
import PublicProfileLayout from './layouts/public-profile-layout'
import { EnterpriseHeadProvider } from './providers/enterprise-head-provider'

const convert =
  (queryClient: QueryClient, suspense = false) =>
  (m: any) => {
    const { clientLoader, clientAction, default: Component, ...rest } = m
    const WrappedComponent = suspense
      ? (props: any) => (
          <GenericSuspense style={props.style || 'fullscreen'}>
            <Component {...props} />
          </GenericSuspense>
        )
      : Component

    return {
      ...rest,
      loader: clientLoader?.(queryClient),
      action: clientAction?.(queryClient),
      Component: WrappedComponent,
    }
  }

const RootLogic = () => {
  usePageView()
  // Initialize tracking parameters (GCLID and UTM)
  useTrackingParams()

  return (
    <>
      <Outlet />
      <ScrollRestoration />
    </>
  )
}

export const createAppRouter = (queryClient: QueryClient): ReturnType<typeof createBrowserRouter> => {
  const sentryCreateBrowserRouter = wrapCreateBrowserRouterV6(createBrowserRouter)

  return sentryCreateBrowserRouter([
    {
      // Root Logic -> Log page views and transition animations
      element: (
        <ErrorBoundaryWrapper>
          <RootLogic />
        </ErrorBoundaryWrapper>
      ),
      errorElement: <ErrorPage />,
      children: [
        // Auth & Onboarding
        {
          element: <AuthLayout />,
          children: [
            {
              path: paths.auth.login.path,
              lazy: () => import('./routes/auth/login').then(convert(queryClient)),
            },
            {
              path: paths.auth.signup.path,
              lazy: () => import('./routes/auth/signup').then(convert(queryClient)),
            },
            {
              path: paths.auth.resetPassword.request.path,
              lazy: () => import('./routes/auth/request-password-reset').then(convert(queryClient)),
            },
            {
              path: paths.auth.resetPassword.confirm.path,
              lazy: () => import('./routes/auth/reset-password').then(convert(queryClient)),
            },
            {
              path: paths.auth.confirmAccount.path,
              lazy: () => import('./routes/auth/confirm-signup').then(convert(queryClient)),
            },
            {
              path: paths.auth.onboarding.path,
              lazy: () => import('./routes/auth/onboarding').then(convert(queryClient)),
            },
          ],
        },

        // Under Construction
        {
          children: [
            {
              path: paths.alert.path,
              lazy: () => import('./routes/alert/index').then(convert(queryClient)),
            },
          ],
        },

        // Consumer Auth
        {
          children: [
            {
              path: paths.consumer.auth.login.path,
              lazy: () => import('./routes/auth/consumer/login').then(convert(queryClient)),
            },
            {
              path: paths.consumer.auth.signup.path,
              lazy: () => import('./routes/auth/consumer/signup').then(convert(queryClient)),
            },
          ],
        },

        // Consumer Authenticated Pages
        {
          element: (
            <GenericSuspense style="fullscreen">
              <ConsumerAuthGuard>
                <Outlet />
              </ConsumerAuthGuard>
            </GenericSuspense>
          ),
          children: [
            {
              path: paths.client.index.path,
              lazy: () => import('./routes/client/index').then(convert(queryClient)),
            },
            {
              path: paths.client.business.path,
              lazy: () => import('./routes/client/business').then(convert(queryClient)),
            },
          ],
        },

        // Enterprise Auth
        {
          element: (
            <EnterpriseSignInFlowGuard>
              <EnterpriseAuthLayout>
                <Outlet />
              </EnterpriseAuthLayout>
            </EnterpriseSignInFlowGuard>
          ),
          children: [
            {
              path: paths.enterprise.login.path,
              lazy: () => import('./routes/auth/enterprise/login').then(convert(queryClient)),
            },
            {
              path: paths.enterprise.signup.path,
              lazy: () => import('./routes/auth/enterprise/signup').then(convert(queryClient)),
            },
            {
              path: paths.enterprise.confirmSignup.path,
              lazy: () => import('./routes/auth/enterprise/confirm-signup').then(convert(queryClient)),
            },
          ],
        },

        // Enterprise Onboarding
        {
          element: (
            <EnterpriseHeadProvider>
              <EnterpriseAuthGuard>
                <EnterpriseOnboardingPageGuard>
                  <EnterpriseAuthLayout>
                    <Outlet />
                  </EnterpriseAuthLayout>
                </EnterpriseOnboardingPageGuard>
              </EnterpriseAuthGuard>
            </EnterpriseHeadProvider>
          ),
          children: [
            {
              path: paths.enterprise.onboarding.path,
              lazy: () => import('./routes/auth/enterprise/onboarding').then(convert(queryClient)),
            },
          ],
        },

        // Enterprise Home Public Page
        {
          element: (
            <EnterpriseHeadProvider>
              <Outlet />
            </EnterpriseHeadProvider>
          ),
          children: [
            {
              path: paths.enterprise.path,
              lazy: () => import('./routes/enterprise/index').then(convert(queryClient)),
              errorElement: (
                <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />
              ),
            },
          ],
        },

        // Enterprise Authenticated Pages
        {
          element: (
            <EnterpriseHeadProvider>
              <EnterpriseAuthGuard>
                <EnterpriseOnboardingGuard>
                  <Outlet />
                </EnterpriseOnboardingGuard>
              </EnterpriseAuthGuard>
            </EnterpriseHeadProvider>
          ),
          children: [
            {
              path: paths.enterprise.dashboard.path,
              lazy: () => import('./routes/enterprise/index').then(convert(queryClient)),
            },
          ],
        },

        // TEMP DELETE ACCOUNT PAGE
        {
          element: (
            <ErrorBoundaryWrapper style="custom" fallback={<AppErrorBoundary />}>
              <Outlet />
            </ErrorBoundaryWrapper>
          ),
          children: [
            {
              path: paths.deleteAccount.path,
              lazy: () => import('./routes/delete-account/index').then(convert(queryClient)),
            },
            {
              element: (
                <AuthGuard>
                  <Outlet />
                </AuthGuard>
              ),
              children: [
                {
                  path: paths.deleteAccount.web.path,
                  lazy: () => import('./routes/delete-account/delete-account-page').then(convert(queryClient)),
                },
              ],
            },
          ],
        },

        // Download App Page
        {
          path: paths.downloadApp.path,
          lazy: () => import('./routes/download-app/index').then(convert(queryClient)),
        },

        // Authenticated Pages
        {
          element: (
            <ErrorBoundaryWrapper style="custom" fallback={<AppErrorBoundary />}>
              <BlockWebGuard>
                <AuthGuard>
                  <OnboardingGuard>
                    <Outlet />
                  </OnboardingGuard>
                </AuthGuard>
              </BlockWebGuard>
            </ErrorBoundaryWrapper>
          ),
          children: [
            // Default Layout (Show both header and footer)
            {
              element: <AuthenticatedLayout />,
              children: [
                {
                  path: paths.home.path,
                  lazy: () => import('./routes/home').then(convert(queryClient)),
                },
                {
                  path: paths.search.path,
                  lazy: () => import('./routes/search').then(convert(queryClient)),
                },
                {
                  path: paths.feed.post.create.path,
                  lazy: () => import('./routes/feed/create-post').then(convert(queryClient)),
                },
                {
                  path: paths.referrals.getMatched.path,
                  element: <Navigate to={paths.home.getHref()} replace />,
                },
                {
                  path: paths.settings.path,
                  lazy: () => import('./routes/settings').then(convert(queryClient)),
                },
                {
                  path: paths.settings.security.path,
                  lazy: () => import('./routes/settings/security-settings-page').then(convert(queryClient)),
                },
                {
                  path: paths.settings.personalInfo.path,
                  lazy: () => import('./routes/settings/personal-info-settings-page').then(convert(queryClient)),
                },
                {
                  path: paths.settings.personalInfoFields.path,
                  lazy: () => import('./routes/settings/edit-personal-info-fields-page').then(convert(queryClient)),
                },
                {
                  path: paths.settings.verifyEmail.path,
                  lazy: () => import('./routes/settings/verify-email-page').then(convert(queryClient)),
                },
                {
                  path: paths.admin.path,
                  lazy: () => import('./routes/admin').then(convert(queryClient)),
                },
                {
                  path: paths.support.path,
                  lazy: () => import('./routes/support').then(convert(queryClient)),
                },
                {
                  path: paths.subscriptions.success.path,
                  lazy: () => import('./routes/subscriptions/success').then(convert(queryClient)),
                },
                {
                  path: paths.subscriptions.sessionCanceled.path,
                  lazy: () => import('./routes/subscriptions/session-canceled').then(convert(queryClient)),
                },
                {
                  path: paths.settings.subscriptions.path,
                  lazy: () => import('./routes/settings/subscriptions-page').then(convert(queryClient)),
                },
                {
                  path: paths.assistant.history.path,
                  lazy: () => import('./routes/assistant/history').then(convert(queryClient)),
                },
                {
                  path: paths.assistant.customize.path,
                  lazy: () => import('./routes/assistant/customize').then(convert(queryClient)),
                },
                {
                  path: paths.leads.detail.path,
                  lazy: () => import('./routes/business/leads/lead-page').then(convert(queryClient)),
                },
              ],
            },
            // Hide Header Layout
            {
              element: <AuthenticatedLayout hideHeader />,
              children: [
                {
                  path: paths.notifications.path,
                  lazy: () => import('./routes/notifications').then(convert(queryClient)),
                },
                {
                  path: paths.partnerMatch.path,
                  lazy: () => import('./routes/partner-match/list').then(convert(queryClient)),
                },
                {
                  path: paths.network.path,
                  lazy: () => import('./routes/network').then(convert(queryClient)),
                },
                {
                  path: paths.chats.list.path,
                  lazy: () => import('./routes/chats/list').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.referrals.list.path,
                  lazy: () => import('./routes/business/referrals/list').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                  children: [
                    {
                      path: paths.referrals.list.path,
                      element: <Navigate to={paths.referrals.list.received.path} replace />,
                    },
                    {
                      path: paths.referrals.list.received.path,
                      lazy: () => import('./routes/business/received').then(convert(queryClient)),
                    },
                    {
                      path: paths.referrals.list.shared.path,
                      lazy: () => import('./routes/business/referrals/list/shared').then(convert(queryClient)),
                    },
                  ],
                },
              ],
            },
            // Hide Footer Layout
            {
              element: <AuthenticatedLayout hideFooter />,
              children: [
                {
                  path: paths.referrals.getMatched.info.path,
                  lazy: () => import('./routes/business/referrals/get-matched/info').then(convert(queryClient)),
                },
                {
                  path: paths.referrals.getMatched.results.path,
                  lazy: () => import('./routes/business/referrals/get-matched/results').then(convert(queryClient)),
                },
                {
                  path: paths.feed.path,
                  lazy: () => import('./routes/feed/timeline').then(convert(queryClient, true)),
                },
                {
                  path: paths.settings.notifications.path,
                  lazy: () => import('./routes/settings/notification-settings-page').then(convert(queryClient)),
                },
                {
                  path: paths.users.editProfile.path,
                  lazy: () => import('./routes/user/edit/edit-profile-page').then(convert(queryClient)),
                },
                {
                  path: paths.users.editLicenses.path,
                  lazy: () => import('./routes/user/edit/edit-licenses-page').then(convert(queryClient)),
                },
                {
                  path: paths.settings.profilePrivacy.path,
                  lazy: () => import('./routes/settings/profile-privacy-settings-page').then(convert(queryClient)),
                },
                {
                  path: paths.referrals.detail.referralShare.path,
                  element: <ReceivedReferralSharePage />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.referrals.detail.referral.path,
                  element: <SentReferralPage />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.referrals.send.path,
                  lazy: () => import('./routes/business/referrals/send').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.users.points.path,
                  element: <Navigate to={paths.home.getHref()} replace />,
                },
                {
                  path: paths.assistant.path,
                  lazy: () => import('./routes/assistant').then(convert(queryClient)),
                },
                {
                  path: paths.assistant.conversation.path,
                  lazy: () => import('./routes/assistant/conversation').then(convert(queryClient)),
                },
              ],
            },
            // Hide Both Layout
            {
              element: <AuthenticatedLayout hideHeader hideFooter />,
              children: [
                {
                  path: paths.chats.conversation.referral.path,
                  lazy: () => import('./routes/chats/conversation/referral').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.chats.conversation.match.path,
                  lazy: () => import('./routes/chats/conversation/match').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.chats.conversation.network.path,
                  lazy: () => import('./routes/chats/conversation/network').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.chats.conversation.sneak.path,
                  lazy: () => import('./routes/chats/conversation/sneak').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.invite.path,
                  lazy: () => import('./routes/invite').then(convert(queryClient)),
                },
                {
                  path: paths.feed.post.path,
                  lazy: () => import('./routes/feed/post').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.feed.comment.path,
                  lazy: () => import('./routes/feed/comment').then(convert(queryClient)),
                  errorElement: <ErrorPage />,
                },
                {
                  path: paths.partnerMatch.preferences.path,
                  lazy: () => import('./routes/partner-match/preferences').then(convert(queryClient)),
                },
                {
                  path: paths.leads.grades.path,
                  lazy: () => import('./routes/business/leads/how-grades-work').then(convert(queryClient)),
                },
              ],
            },
            {
              path: paths.users.completeProfile.path,
              lazy: () => import('./routes/user/edit/complete-profile').then(convert(queryClient)),
            },
            {
              path: paths.partnerMatch.unlock.path,
              lazy: () => import('./routes/partner-match/unlock').then(convert(queryClient)),
            },
            {
              path: paths.recap.path,
              lazy: () => import('./routes/recap').then(convert(queryClient)),
            },
            {
              path: paths.subscriptions.path,
              lazy: () => import('./routes/subscriptions/subscription-page').then(convert(queryClient)),
            },
            {
              path: paths.subscriptions.path,
              lazy: () => import('./routes/subscriptions/subscription-page').then(convert(queryClient)),
            },
          ],
        },
        // Public Pages
        {
          element: (
            <ErrorBoundaryWrapper>
              <PublicLayout />
            </ErrorBoundaryWrapper>
          ),
          children: [
            // unsubscribe only used via email link
            {
              path: paths.users.unsubscribe.path,
              lazy: () => import('./routes/email-actions/unsubscribe').then(convert(queryClient)),
            },
          ],
        },
        {
          path: '/:username',
          element: <UserProfile />,
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.users.publicProfile.singleListing.path,
          lazy: () => import('./routes/user/profile/public/single-listing').then(convert(queryClient)),
        },
        {
          path: paths.users.publicProfile.portfolio.single.path,
          lazy: () => import('./routes/user/profile/public/single-portfolio').then(convert(queryClient)),
        },
        // User Profile (Public & Private) -> Layouts within the pages
        {
          path: '/:username/public',
          element: (
            <PublicProfileLayout profile>
              <Outlet />
            </PublicProfileLayout>
          ),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
          children: [
            {
              path: paths.users.publicProfile.path,
              lazy: () => import('./routes/user/profile/public-user-profile').then(convert(queryClient)),
            },
            {
              path: paths.users.publicProfile.listings.path,
              lazy: () => import('./routes/user/profile/public/listings').then(convert(queryClient)),
            },
            {
              path: paths.users.publicProfile.reviews.path,
              lazy: () => import('./routes/user/profile/public/reviews').then(convert(queryClient)),
            },
            {
              path: paths.users.publicProfile.activity.path,
              lazy: () => import('./routes/user/profile/public/activity').then(convert(queryClient)),
            },
            {
              path: paths.users.publicProfile.portfolio.path,
              lazy: () => import('./routes/user/profile/public/portfolio').then(convert(queryClient)),
            },
          ],
        },
        {
          path: '/:username/message',
          lazy: () => import('./routes/user/message').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          element: <Outlet />,
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
          children: [
            {
              path: paths.marketplace.path,
              lazy: () => import('./routes/marketplace').then(convert(queryClient)),
            },
            {
              path: paths.marketplace.details.path,
              lazy: () => import('./routes/marketplace').then(convert(queryClient)),
            },
          ],
        },
        {
          path: paths.marketplace.details.path,
          lazy: () => import('./routes/marketplace').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.marketplace.filters.path,
          lazy: () => import('./routes/marketplace/filters').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.marketplace.instantMatch.path,
          lazy: () => import('./routes/marketplace/instant-match').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.marketplace.instantMatch.userId.path,
          lazy: () => import('./routes/marketplace/instant-match/user/user-id').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.marketplace.instantMatch.profession.path,
          lazy: () => import('./routes/marketplace/instant-match/profession').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.marketplace.instantMatch.mortgageCalculator.path,
          lazy: () => import('./routes/marketplace/instant-match/mortgage-calculator').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        // Error Page
        {
          path: paths.error.notFound.path,
          lazy: () => import('./routes/error').then(convert(queryClient)),
        },
        // Landing Pages
        {
          path: paths.landing.google.path,
          lazy: () => import('./routes/landing/google-ads').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.google.financingAd.path,
          lazy: () => import('./routes/landing/google-financing-ad').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.google.closingFrustrations.path,
          lazy: () => import('./routes/landing/google-closing-frustrations').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.google.inventoryCompetition.path,
          lazy: () => import('./routes/landing/google-inventory-competition').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.google.postPurchaseRegretMatch.path,
          lazy: () => import('./routes/landing/google-post-purchase-regret-match').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.google.realEstateMatch.path,
          lazy: () => import('./routes/landing/google-real-estate-match').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.meta.path,
          lazy: () => import('./routes/landing/meta-ads').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.landing.vsl.path,
          lazy: () => import('./routes/landing/vsl').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        {
          path: paths.root.path,
          lazy: () => import('./routes/index').then(convert(queryClient)),
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
        },
        // Blog Pages
        {
          path: paths.blog.list.path,
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
          lazy: () => import('./routes/blog/list').then(convert(queryClient)),
        },
        {
          path: paths.blog.post.path,
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
          lazy: () => import('./routes/blog/post').then(convert(queryClient)),
        },
        {
          path: paths.blog.studio.path,
          errorElement: <ErrorPage title="Not Found" description="We couldn't find the page you were looking for." />,
          lazy: () => import('./routes/blog/studio').then(convert(queryClient)),
        },
        // Fallback route -> Redirect to home
        {
          path: '*',
          element: <Navigate to={paths.root.getHref()} />,
        },
      ],
    },
  ])
}

export const AppRouter = () => {
  const queryClient = useQueryClient()
  const router = useMemo(() => createAppRouter(queryClient), [queryClient])

  return <RouterProvider router={router} />
}
